import { API_BASE_URL } from '@/config/endpoints';
import axios, { AxiosRequestConfig, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/store/authStore';
import { authTokenRefreshCreate } from '@/gen-api/api';

/**
 * We need a single, reusable Axios client so that response interceptors can
 * catch 401 errors, refresh the JWT, and replay the failed requests.  We keep
 * the public API identical (`customInstance<T>(config)`) so that the rest of
 * the generated SDK continues to work unchanged.
 */

// --------------------------------------------------------------------------------
// Singleton axios instance
// --------------------------------------------------------------------------------

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Add timeout to prevent hanging requests
  timeout: 200000, // 200 second timeout
});

// --------------------------------------------------------------------------------
// Request interceptor – always attach latest access token & workspace header
// --------------------------------------------------------------------------------

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const state = useAuthStore.getState();
    const accessToken = state.credentials?.accessToken;
    const workspaceId = state.currentWorkspace?.id;

    if (accessToken) {
      config.headers.set('Authorization', `Bearer ${accessToken}`);
    }
    if (workspaceId) {
      config.headers.set('X-Workspace-ID', String(workspaceId));
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// --------------------------------------------------------------------------------
// Response interceptor – handle 401 → refresh token → retry logic
// --------------------------------------------------------------------------------

let isRefreshing = false;
type QueueItem = {
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
};
let failedQueue: QueueItem[] = [];

const processQueue = (error: unknown | null, token: string | null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const { response, config, code } = error;

    // Handle network errors that might be mistaken for CORS issues
    if (!response) {
      if (code === 'ECONNABORTED') {
        console.error('Request timeout - this might appear as a CORS error in some browsers');
      } else if (code === 'ERR_NETWORK') {
        console.error('Network error - check your API URL and CORS configuration:', API_BASE_URL);
      }
      return Promise.reject(error);
    }

    // If no response or status !== 401, just propagate error
    if (response.status !== 401 || !config) {
      return Promise.reject(error);
    }

    // Prevent infinite loop: do not try to refresh if the request is itself the refresh endpoint
    if (config.url?.includes('/auth/token/refresh/')) {
      return Promise.reject(error);
    }

    // Mark the request so it isn't retried multiple times
    // (Type cast because AxiosRequestConfig by default doesn't include custom props)
    const originalRequest = config as AxiosRequestConfig & { _retry?: boolean };
    if (originalRequest._retry) {
      return Promise.reject(error);
    }
    originalRequest._retry = true;

    // Handle refresh logic
    const storeState = useAuthStore.getState();
    const refreshToken = storeState.credentials?.refreshToken;

    if (!refreshToken) {
      // No refresh token → log out
      storeState.signOut();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Queue the request until the refresh is done
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token: string) => {
            if (!originalRequest.headers) originalRequest.headers = {};
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            resolve(api(originalRequest));
          },
          reject: (err: unknown) => reject(err),
        });
      });
    }

    isRefreshing = true;

    try {
      const refreshResponse = await authTokenRefreshCreate({ refresh: refreshToken });
      const newAccessToken = (refreshResponse as unknown as { access: string }).access;

      // Update store with new token
      const current = useAuthStore.getState();
      if (current.credentials) {
        useAuthStore.setState({
          credentials: {
            ...current.credentials,
            accessToken: newAccessToken,
          },
        });
      }

      processQueue(null, newAccessToken);

      // Retry the original request with new token
      if (!originalRequest.headers) originalRequest.headers = {};
      originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;

      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      useAuthStore.getState().signOut();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// --------------------------------------------------------------------------------
// Public helper used by generated SDK
// --------------------------------------------------------------------------------

export const customInstance = <T>(config: AxiosRequestConfig) => {
  return api.request<T>(config).then(({ data }) => data);
};

// Helper for fetching blobs (images, PDFs, etc) with proper auth headers
// Uses the axios instance so it benefits from token refresh interceptor
export const fetchBlob = async (url: string): Promise<Blob> => {
  const response = await api.request({
    url,
    method: 'GET',
    responseType: 'blob',
  });

  return response.data;
};

/** Download the workspace Teams app ZIP (binary POST). */
export const downloadTeamsPackage = async (
  workspaceId: string,
  options?: { fallbackName?: string }
): Promise<{ blob: Blob; filename: string }> => {
  try {
    const response = await api.request<Blob>({
      url: `/workspaces/${workspaceId}/teams/package/`,
      method: 'POST',
      responseType: 'blob',
    });

    const disposition = String(response.headers['content-disposition'] || '');
    const match = /filename\*?=(?:UTF-8''|")?([^";]+)"?/i.exec(disposition);
    const fromHeader = match?.[1] ? decodeURIComponent(match[1].trim()) : '';
    const safeFallback =
      (options?.fallbackName || 'workspace')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'workspace';
    const filename = fromHeader || `vigilant-teams-${safeFallback}.zip`;

    return { blob: response.data, filename };
  } catch (error: unknown) {
    const axiosError = error as {
      response?: { data?: Blob; status?: number };
      message?: string;
    };
    const data = axiosError.response?.data;
    if (data instanceof Blob) {
      try {
        const text = await data.text();
        const parsed = JSON.parse(text) as { detail?: string };
        if (parsed.detail) {
          throw Object.assign(new Error(parsed.detail), {
            response: { data: { detail: parsed.detail } },
          });
        }
      } catch (inner) {
        if (inner && typeof inner === 'object' && 'response' in inner) {
          throw inner;
        }
      }
    }
    throw error;
  }
};

// Export the axios instance for direct usage in components that need custom API calls
export default api;
